import { cn } from "@/lib/utils";
import { ButtonHTMLAttributes, forwardRef } from "react";
import { Icon } from "./icon";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline" | "tertiary";
type Size = "sm" | "md" | "lg";

const variants: Record<Variant, string> = {
  primary:
    "bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg active:scale-[0.98]",
  secondary: "bg-secondary text-white hover:bg-secondary/90",
  ghost: "bg-transparent text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface",
  danger: "bg-error text-white hover:bg-error/90",
  outline:
    "border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-low",
  tertiary: "bg-tertiary text-white hover:bg-tertiary/90",
};

const sizes: Record<Size, string> = {
  sm: "h-8 px-3 text-label-md",
  md: "h-9 px-4 text-body-md",
  lg: "h-10 px-5 text-body-md",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      loading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
        variants[variant],
        sizes[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <Icon name="progress_activity" size="md" className="animate-spin" />
      ) : null}
      {children}
    </button>
  ),
);
Button.displayName = "Button";
